import { authService } from "@/services/authService";
import { useAuthStore } from "@/store/authStore";
import { broadcastAuthLoggedOut } from "@/lib/authSync";
import { getTenantRootDomain, getTenantSubdomain, isTenantRootDomain } from "@/lib/tenant";

export const PUBLIC_TENANT_ENTRY_PATHS = [
  "/",
  "/login",
  "/register",
  "/privacy-policy",
  "/terms-of-use",
] as const;

export type TenantRecoveryReason = "workspace_not_found" | "wrong_workspace";

const WORKSPACE_ERROR_MARKERS = [
  "Wrong workspace",
  "Workspace not found",
] as const;

export const isPublicTenantEntryPath = (pathname: string): boolean =>
  PUBLIC_TENANT_ENTRY_PATHS.includes(pathname as (typeof PUBLIC_TENANT_ENTRY_PATHS)[number]);

export const isWorkspaceError = (detail: string | undefined | null): boolean => {
  if (!detail) {
    return false;
  }
  return WORKSPACE_ERROR_MARKERS.some((marker) => detail.includes(marker));
};

const triggerDevLogoutOnHost = (targetHost: string) => {
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : "";
  const img = new Image();
  img.src = `${protocol}//${targetHost}${port}/api/v1/auth/logout`;
};

export const buildTenantRootUrl = (
  pathname: string,
  search = "",
  hostname = typeof window !== "undefined" ? window.location.hostname : "",
): string => {
  const rootDomain = getTenantRootDomain(hostname);
  if (!rootDomain) {
    return `${pathname}${search}`;
  }

  const protocol = typeof window !== "undefined" ? window.location.protocol : "https:";
  const port = typeof window !== "undefined" && window.location.port
    ? `:${window.location.port}`
    : "";

  return `${protocol}//${rootDomain}${port}${pathname}${search}`;
};

export const redirectToTenantRoot = (pathname: string, search = "") => {
  window.location.replace(buildTenantRootUrl(pathname, search));
};

export const performHostLogout = async () => {
  const hostname = window.location.hostname;
  const rootDomain = getTenantRootDomain(hostname);
  const isLocalhostRoot = rootDomain === "localhost";
  const currentSubdomain = getTenantSubdomain(hostname);
  const currentUser = useAuthStore.getState().user;
  let otherHost: string | null = null;

  if (isLocalhostRoot) {
    if (currentSubdomain) {
      otherHost = rootDomain;
    } else if (currentUser?.company_subdomain) {
      otherHost = `${currentUser.company_subdomain}.${rootDomain}`;
    }
  }

  try {
    await authService.logout();
  } catch {
    // Ignore logout errors; clear client state regardless.
  }

  if (otherHost && otherHost !== hostname) {
    triggerDevLogoutOnHost(otherHost);
  }

  useAuthStore.getState().logout();
  broadcastAuthLoggedOut();
};

export const recoverFromInvalidTenant = async ({
  reason,
}: {
  reason: TenantRecoveryReason;
}) => {
  if (typeof window === "undefined") {
    return;
  }

  const hostname = window.location.hostname;
  if (isTenantRootDomain(hostname)) {
    return;
  }

  const pathname = window.location.pathname;
  const search = window.location.search;
  const isPublicEntry = isPublicTenantEntryPath(pathname);
  const requiresLogout = reason === "wrong_workspace" || !isPublicEntry;

  if (requiresLogout) {
    await performHostLogout();
    redirectToTenantRoot("/login", `?reason=${reason}`);
    return;
  }

  redirectToTenantRoot(pathname, search);
};
